import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Clock, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Show the warning when this many seconds remain on the access token.
const WARN_WINDOW_SEC = 5 * 60;

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch (_) { return null; }
}

/**
 * Subtle idle-session banner. Polls the token's exp claim every 30s. When
 * <=5 min remain, shows a "You'll be signed out in Xm Ys" nudge with a
 * "Stay signed in" button that calls /auth/refresh transparently.
 */
const SessionExpiryWarning = () => {
  const { user, logout } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const tick = useCallback(() => {
    const tok = sessionStorage.getItem('access_token');
    if (!tok) { setSecondsLeft(null); return; }
    const payload = decodeJwt(tok);
    if (!payload?.exp) { setSecondsLeft(null); return; }
    const remaining = Math.floor(payload.exp - Date.now() / 1000);
    setSecondsLeft(remaining);
  }, []);

  useEffect(() => {
    if (!user) return;
    tick();
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [user, tick]);

  const stayIn = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, _skipAuthRetry: true });
      if (data?.access_token) sessionStorage.setItem('access_token', data.access_token);
      toast.success('Session refreshed — you have another hour');
      tick();
    } catch (_) {
      toast.error('Could not refresh — please sign in again');
      logout();
    } finally { setRefreshing(false); }
  };

  if (secondsLeft === null || secondsLeft > WARN_WINDOW_SEC || secondsLeft <= 0) return null;
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border border-amber-200 bg-amber-50 shadow-lg p-3 flex items-start gap-3" data-testid="session-warning">
      <Clock className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">You'll be signed out soon</p>
        <p className="text-xs text-amber-800 mt-0.5">
          Your session expires in <strong data-testid="session-warning-countdown">{m}m {s.toString().padStart(2, '0')}s</strong>.
          Save any open work.
        </p>
        <button
          type="button"
          onClick={stayIn}
          disabled={refreshing}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-white bg-amber-700 hover:bg-amber-800 rounded px-2.5 py-1 disabled:opacity-60"
          data-testid="session-warning-stay-in"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Stay signed in'}
        </button>
      </div>
    </div>
  );
};

export default SessionExpiryWarning;
